import config from 'config';
import { PaginatedParams } from 'core/http/pagination';
import { ListPayload, ListResponse, SuccessResponse } from 'core/http/response';
import { GoodsShipmentItem as EtdGoodsShipmentItem } from 'store/declarations/ireland/electronic-transport-document';
import { EnsGoodsShipmentItem } from 'store/declarations/ireland/entry-summary-declaration';
import { IrelandExportItem } from 'store/declarations/ireland/export-declaration';
import { GoodsShipmentItem as H7GoodsShipmentItem } from 'store/declarations/ireland/h7-import-declaration';
import {
    GoodsShipmentItem,
    GoodsShipmentItem as H1GoodsShipmentItem,
} from 'store/declarations/ireland/import-declaration';
import { CdsGoodsItemEntry } from 'store/declarations/uk/cds-declaration';
import {
    ICS2GoodsItem,
    ICS2GoodsItemCategory,
    ICS2GoodsItemResponse,
} from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { Declaration } from 'store/declarations/declaration';
import axiosClient from '../../config/axios';
import { ArrivalAtExitDeclaration } from '../declarations/ireland/arrival-at-exit-declaration';
import { AxiosResponse } from 'axios';
import { PresignedUrlResponse } from 'store/documents/document';

export const createIrelandImportProduct = (
    product: H1GoodsShipmentItem,
    declarationId: string
): Promise<H1GoodsShipmentItem> =>
    axiosClient
        .post<SuccessResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/products`,
            product
        )
        .then((response) => response.data.payload);

export const updateIrelandImportProduct = (
    product: H1GoodsShipmentItem,
    declarationId: string,
    productId: string
): Promise<H1GoodsShipmentItem> =>
    axiosClient
        .put<SuccessResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const deleteIrelandImportProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => {
            return response.data;
        });

export const deleteIrelandImportProductsBulk = (productIds: string[], declarationId: string): Promise<any> =>
    axiosClient
        .delete<SuccessResponse<any>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/products-bulk`,
            {
                params: { productIds },
                paramsSerializer: () => productIds.map((id) => `productIds=${encodeURIComponent(id)}`).join('&'),
            }
        )
        .then((response) => response.data);

export const createIrelandH7ImportProduct = (
    product: H7GoodsShipmentItem,
    declarationId: string
): Promise<H7GoodsShipmentItem> =>
    axiosClient
        .post<SuccessResponse<H7GoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/h7/products`,
            product
        )
        .then((response) => response.data.payload);

export const deleteIrelandH7ImportProduct = (
    productId: string,
    declarationId: string
): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/h7/products/${productId}`
        )
        .then((response) => {
            return response.data;
        });

export const updateIrelandH7ImportProduct = (
    product: H7GoodsShipmentItem,
    declarationId: string,
    productId: string
): Promise<H7GoodsShipmentItem> =>
    axiosClient
        .put<SuccessResponse<H7GoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/h7/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const createIrelandExportProduct = (
    product: IrelandExportItem,
    declarationId: string
): Promise<IrelandExportItem> =>
    axiosClient
        .post<SuccessResponse<IrelandExportItem>>(
            `${config.declarationsUrl}/ie/export/declarations/${declarationId}/products`,
            product
        )
        .then((response) => response.data.payload);

export type ArrivalAtExitProduct = ArrivalAtExitDeclaration['goodsShipment']['goodsItem'][0];
export const createArrivalAtExitProduct = (
    product: ArrivalAtExitProduct,
    declarationId: string
): Promise<ArrivalAtExitProduct> =>
    axiosClient
        .post<SuccessResponse<ArrivalAtExitProduct>>(
            `${config.declarationsUrl}/ie/arrival-exit/declarations/${declarationId}/products`,
            product
        )
        .then((response) => response.data.payload);

export const updateIrelandExportProduct = (
    product: IrelandExportItem,
    declarationId: string,
    productId: string
): Promise<IrelandExportItem> =>
    axiosClient
        .put<SuccessResponse<IrelandExportItem>>(
            `${config.declarationsUrl}/ie/export/declarations/${declarationId}/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const updateArrivalAtExitProduct = (
    product: ArrivalAtExitProduct,
    declarationId: string,
    productId: string
): Promise<ArrivalAtExitProduct> =>
    axiosClient
        .put<SuccessResponse<ArrivalAtExitProduct>>(
            `${config.declarationsUrl}/ie/arrival-exit/declarations/${declarationId}/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const deleteIrelandExportProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/ie/export/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => {
            return response.data;
        });

export const deleteArrivalAtExitProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/ie/arrival-exit/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => {
            return response.data;
        });

export const createCdsProduct = (product: CdsGoodsItemEntry, declarationId: string): Promise<CdsGoodsItemEntry> =>
    axiosClient
        .post<SuccessResponse<CdsGoodsItemEntry>>(
            `${config.declarationsUrl}/uk/declarations/${declarationId}/products`,
            product
        )
        .then((response) => response.data.payload);

export const updateCdsProduct = (
    product: CdsGoodsItemEntry,
    declarationId: string,
    productId: string
): Promise<CdsGoodsItemEntry> =>
    axiosClient
        .put<SuccessResponse<CdsGoodsItemEntry>>(
            `${config.declarationsUrl}/uk/declarations/${declarationId}/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const deleteCdsProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/uk/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => {
            return response.data;
        });

export const createIrelandEnsProduct = (
    product: EnsGoodsShipmentItem,
    declarationId: string
): Promise<EnsGoodsShipmentItem> =>
    axiosClient
        .post<SuccessResponse<EnsGoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/ens/declarations/${declarationId}/products`,
            product
        )
        .then((response) => response.data.payload);

export const updateIrelandEnsProduct = (
    product: EnsGoodsShipmentItem,
    declarationId: string
): Promise<EnsGoodsShipmentItem> =>
    axiosClient
        .put<SuccessResponse<EnsGoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/ens/declarations/${declarationId}/products/${product.id}`,
            product
        )
        .then((response) => response.data.payload);

export const deleteIrelandEnsProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/ireland/ens/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data);

export const getIrelandEnsProduct = (declarationId: string, productId: string): Promise<EnsGoodsShipmentItem> =>
    axiosClient
        .get<SuccessResponse<EnsGoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/ens/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const listIrelandEnsProducts = (
    declarationId: string,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<EnsGoodsShipmentItem>> =>
    axiosClient
        .get<ListResponse<EnsGoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/ens/declarations/${declarationId}/products`,
            { params }
        )
        .then((response) => response.data.payload);

const ics2EnsProductsUrl = (declarationId: string) =>
    `${config.declarationsUrl}/eu/ics2/ens/declarations/${declarationId}/products`;

export const createIcs2EnsProduct = (
    product: ICS2GoodsItem,
    declarationId: string,
    category: ICS2GoodsItemCategory,
    houseConsignmentId?: string
): Promise<ICS2GoodsItemResponse> =>
    axiosClient
        .post<SuccessResponse<ICS2GoodsItemResponse>>(
            ics2EnsProductsUrl(declarationId),
            { payload: product },
            { params: { category, houseConsignmentId } }
        )
        .then((response) => response.data.payload);

/**
 * Passing `houseConsignmentId` moves a house level item to that house consignment. Leaving it out
 * keeps the item where it is.
 */
export const updateIcs2EnsProduct = (
    product: ICS2GoodsItem,
    declarationId: string,
    productId: string,
    houseConsignmentId?: string
): Promise<ICS2GoodsItemResponse> =>
    axiosClient
        .put<SuccessResponse<ICS2GoodsItemResponse>>(
            `${ics2EnsProductsUrl(declarationId)}/${productId}`,
            { payload: product },
            { params: { houseConsignmentId } }
        )
        .then((response) => response.data.payload);

export const deleteIcs2EnsProduct = (productId: string, declarationId: string): Promise<Declaration> =>
    axiosClient
        .delete<SuccessResponse<Declaration>>(`${ics2EnsProductsUrl(declarationId)}/${productId}`)
        .then((response) => response.data.payload);

export const getIcs2EnsProduct = (declarationId: string, productId: string): Promise<ICS2GoodsItemResponse> =>
    axiosClient
        .get<SuccessResponse<ICS2GoodsItemResponse>>(`${ics2EnsProductsUrl(declarationId)}/${productId}`)
        .then((response) => response.data.payload);

export const listIcs2EnsProducts = (
    declarationId: string,
    category: ICS2GoodsItemCategory,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<ICS2GoodsItemResponse>> =>
    axiosClient
        .get<ListResponse<ICS2GoodsItemResponse>>(ics2EnsProductsUrl(declarationId), {
            params: { ...params, category },
        })
        .then((response) => response.data.payload);

export const listIrelandImportDeclarationProducts = (
    declarationId: string,
    params?: Partial<PaginatedParams>,
    options?: { signal: AbortSignal }
): Promise<ListPayload<H1GoodsShipmentItem>> => {
    // This endpoint reads the sort field from `sort`; the table column keys are its enum values.
    const { sortParameter, ...rest } = params ?? {};
    const requestParams = { ...rest, sort: sortParameter };

    return axiosClient
        .get<ListResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/products`,
            { params: requestParams, signal: options?.signal }
        )
        .then((response) => response.data.payload);
};

export const getAllIrelandImportDeclarationProducts = async (
    declarationId: string,
    options?: { signal: AbortSignal },
    itemsPerPage = 50
): Promise<GoodsShipmentItem[]> => {
    const allProducts: GoodsShipmentItem[] = [];

    let currentPage = 0;
    let totalPages = +Infinity;

    while (currentPage < totalPages) {
        const response = await listIrelandImportDeclarationProducts(
            declarationId,
            {
                page: currentPage,
                size: itemsPerPage,
            },
            options
        );

        const { list, total } = response;

        if (!list.length) {
            break;
        }

        allProducts.push(...list);

        totalPages = total;
        currentPage += 1;
    }

    return allProducts;
};

export const getIrelandH1Product = (declarationId: string, productId: string): Promise<H1GoodsShipmentItem> =>
    axiosClient
        .get<SuccessResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const listIrelandH7ImportProduct = (
    declarationId: string,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<H1GoodsShipmentItem>> =>
    axiosClient
        .get<ListResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/h7/products`,
            { params }
        )
        .then((response) => response.data.payload);

export const listCdsProducts = (
    declarationId: string,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<CdsGoodsItemEntry>> =>
    axiosClient
        .get<ListResponse<CdsGoodsItemEntry>>(`${config.declarationsUrl}/uk/declarations/${declarationId}/products`, {
            params,
        })
        .then((response) => response.data.payload);

export const getIrelandH7Product = (declarationId: string, productId: string): Promise<H1GoodsShipmentItem> =>
    axiosClient
        .get<SuccessResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/h7/products/${productId}`
        )
        .then((response) => response.data.payload);

export const listIrelandExportProduct = (
    declarationId: string,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<H1GoodsShipmentItem>> =>
    axiosClient
        .get<ListResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ie/export/declarations/${declarationId}/products`,
            { params }
        )
        .then((response) => response.data.payload);

export const listArrivalAtExitProduct = (
    declarationId: string,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<H1GoodsShipmentItem>> =>
    axiosClient
        .get<ListResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ie/arrival-exit/declarations/${declarationId}/products`,
            { params }
        )
        .then((response) => response.data.payload);

export const getIrelandExportProduct = (declarationId: string, productId: string): Promise<H1GoodsShipmentItem> =>
    axiosClient
        .get<SuccessResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ie/export/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const getArrivalAtExitProduct = (declarationId: string, productId: string): Promise<H1GoodsShipmentItem> =>
    axiosClient
        .get<SuccessResponse<H1GoodsShipmentItem>>(
            `${config.declarationsUrl}/ie/arrival-exit/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const getCdsProduct = (declarationId: string, productId: string): Promise<CdsGoodsItemEntry> =>
    axiosClient
        .get<SuccessResponse<CdsGoodsItemEntry>>(
            `${config.declarationsUrl}/uk/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const createIrelandTsdProduct = (product: any, declarationId: string): Promise<any> =>
    axiosClient
        .post<SuccessResponse<any>>(
            `${config.declarationsUrl}/ireland/import/temporary-storage/${declarationId}/products`,
            product
        )
        .then((response) => response.data.payload);

export const createIrelandNctsProduct = (product: any, declarationId: string): Promise<any> =>
    axiosClient
        .post<SuccessResponse<any>>(`${config.declarationsUrl}/ie/ncts/declarations/${declarationId}/products`, product)
        .then((response) => response.data.payload);

export const createUkNctsProduct = (product: any, declarationId: string): Promise<any> =>
    axiosClient
        .post<SuccessResponse<any>>(`${config.declarationsUrl}/uk/ncts/declarations/${declarationId}/products`, product)
        .then((response) => response.data.payload);

export const deleteIrelandTsdProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/ireland/import/temporary-storage/${declarationId}/products/${productId}`
        )
        .then((response) => response.data);

export const deleteIrelandNctsProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/ie/ncts/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data);

export const deleteUkNctsProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(
            `${config.declarationsUrl}/uk/ncts/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data);

export const updateIrelandTsdProduct = (product: any, declarationId: string, productId: string): Promise<any> =>
    axiosClient
        .put<SuccessResponse<any>>(
            `${config.declarationsUrl}/ireland/import/temporary-storage/${declarationId}/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const updateIrelandNctsProduct = (product: any, declarationId: string, productId: string): Promise<any> =>
    axiosClient
        .put<SuccessResponse<any>>(
            `${config.declarationsUrl}/ie/ncts/declarations/${declarationId}/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const updateUkNctsProduct = (product: any, declarationId: string, productId: string): Promise<any> =>
    axiosClient
        .put<SuccessResponse<any>>(
            `${config.declarationsUrl}/uk/ncts/declarations/${declarationId}/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const listIrelandTsdProductsOfDeclaration = (
    declarationId: string,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<any>> =>
    axiosClient
        .get<ListResponse<any>>(
            `${config.declarationsUrl}/ireland/import/temporary-storage/${declarationId}/products`,
            { params }
        )
        .then((response) => response.data.payload);

export const listIrelandNctsProductsOfDeclaration = (
    declarationId: string,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<any>> =>
    axiosClient
        .get<ListResponse<any>>(`${config.declarationsUrl}/ie/ncts/declarations/${declarationId}/products`, { params })
        .then((response) => response.data.payload);

export const listUkNctsProductsOfDeclaration = (
    declarationId: string,
    params?: Partial<PaginatedParams>
): Promise<ListPayload<any>> =>
    axiosClient
        .get<ListResponse<any>>(`${config.declarationsUrl}/uk/ncts/declarations/${declarationId}/products`, { params })
        .then((response) => response.data.payload);

export const getIrelandTsdProduct = (declarationId: string, productId: string): Promise<any> =>
    axiosClient
        .get<SuccessResponse<any>>(
            `${config.declarationsUrl}/ireland/import/temporary-storage/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const getIrelandNctsProduct = (declarationId: string, productId: string): Promise<any> =>
    axiosClient
        .get<SuccessResponse<any>>(
            `${config.declarationsUrl}/ie/ncts/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const getUkNctsProduct = (declarationId: string, productId: string): Promise<any> =>
    axiosClient
        .get<SuccessResponse<any>>(
            `${config.declarationsUrl}/uk/ncts/declarations/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const createIrelandEtdProduct = (
    product: EtdGoodsShipmentItem,
    declarationId: string
): Promise<EtdGoodsShipmentItem> =>
    axiosClient
        .post<SuccessResponse<EtdGoodsShipmentItem>>(
            `${config.declarationsUrl}/ie/import/etd/${declarationId}/products`,
            product
        )
        .then((response) => response.data.payload);

export const updateIrelandEtdProduct = (
    product: EtdGoodsShipmentItem,
    declarationId: string,
    productId: string
): Promise<EtdGoodsShipmentItem> =>
    axiosClient
        .put<SuccessResponse<EtdGoodsShipmentItem>>(
            `${config.declarationsUrl}/ie/import/etd/${declarationId}/products/${productId}`,
            product
        )
        .then((response) => response.data.payload);

export const listIrelandEtdProductsOfDeclaration = (
    declarationId: string
): Promise<ListPayload<EtdGoodsShipmentItem>> =>
    axiosClient
        .get<ListResponse<EtdGoodsShipmentItem>>(`${config.declarationsUrl}/ie/import/etd/${declarationId}/products`)
        .then((response) => response.data.payload);

export const getIrelandEtdProduct = (declarationId: string, productId: string): Promise<EtdGoodsShipmentItem> =>
    axiosClient
        .get<SuccessResponse<EtdGoodsShipmentItem>>(
            `${config.declarationsUrl}/ie/import/etd/${declarationId}/products/${productId}`
        )
        .then((response) => response.data.payload);

export const deleteIrelandEtdProduct = (productId: string, declarationId: string): Promise<SuccessResponse<void>> =>
    axiosClient
        .delete<SuccessResponse<void>>(`${config.declarationsUrl}/ie/import/etd/${declarationId}/products/${productId}`)
        .then((response) => response.data);

export const getBulkUploadItemsPresignedUrl = async (
    declarationId: string,
    fileName: string,
    fileSize: number
): Promise<PresignedUrlResponse> => {
    const response: AxiosResponse<SuccessResponse<PresignedUrlResponse>> = await axiosClient.get(
        `${config.declarationsUrl}/declarations/bulk/${declarationId}/products/upload`,
        {
            params: {
                fileName,
                fileSize,
            },
        }
    );
    return response.data.payload;
};

export const bulkUploadItems = async (
    declarationId: string | undefined,
    s3Link: string | undefined
): Promise<{
    numberOfItems: number;
    itemsPriceSum: number;
}> => {
    if (!declarationId) throw new Error('Declaration id is missing');
    if (!s3Link) throw new Error('s3Link is missing');

    const response: AxiosResponse<
        SuccessResponse<{
            numberOfItems: number;
            itemsPriceSum: number;
        }>
    > = await axiosClient.post(
        `${config.declarationsUrl}/declarations/bulk/${declarationId}/products`,
        {},
        {
            params: {
                declarationId,
                s3Link,
            },
        }
    );

    return response.data.payload;
};

export const getIrelandProductsTotalByDeclarationId = async (declarationId: string): Promise<number | undefined> => {
    const response: AxiosResponse<SuccessResponse<number>> = await axiosClient.get(
        `${config.declarationsUrl}/ireland/import/declarations/${declarationId}/products/item-price-sum`
    );
    return response.data.payload;
};
